"use client";
import { updateDialectAction } from "@/actions/user.actions";
import { Img } from "@/components/Img";
import { LogoSquare } from "@/components/Logo";
import { Column } from "@/components/Stack";
import { Text } from "@/components/Text";
import { EventNamesEnum, LangEnum } from "@/constants/constants";
import { useUserContext } from "@/context/UserProvider";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { GapValuesType } from "@/types/designSystem.types";
import { Dialect } from "@/types/setting.types";
import { getDialectLabelFromDialectObj } from "@/utils/common.helpers";
import { useState } from "react";

export const DialectSelectorContent = ({
  dialect,
  availableDialects,
  gap = 1,
  onRequestClose,
}: {
  dialect: string;
  availableDialects: Dialect[];
  gap?: GapValuesType;
  onRequestClose?: () => void;
}) => {
  const { t } = useClientTranslations();
  const [selectedDialect, setSelectedDialect] = useState(dialect);
  const [isSwitching, setIsSwitching] = useState(false);
  const { trackEvent } = useTrackingAnalytics();
  const { updateUserDialect } = useUserContext();

  const dialectChangeHandler = async () => {
    setIsSwitching(true);

    if (dialect !== selectedDialect) {
      updateUserDialect(selectedDialect);
      trackEvent({
        eventName: EventNamesEnum.CONTENT_CULTURE_UPDATED,
        properties: {
          previous_content_culture: dialect,
          updated_content_culture: selectedDialect,
          updated_from: "home_page",
        },
      });
      await updateDialectAction({
        dialect: selectedDialect,
      });
    } else {
      onRequestClose?.();
    }

    setIsSwitching(false);
  };

  return (
    <div
      className="w-full max-w-[90vw] md:max-w-[50rem] lg:max-w-[60rem]"
      data-testid="dialect-selector-content"
    >
      <Column justify="center" align="center" fullWidth gap={gap}>
        <LogoSquare />
        <Text tag="h1" weight="bold" size={5}>
          {t("user_culture_screen.content_culture.heading")}
        </Text>

        <div className="my-6 max-h-[50vh] w-full overflow-y-auto px-2 md:max-h-[60vh]">
          <div className="grid grid-cols-2 gap-2 md:max-w-[calc(4*221px+3*0.5rem)] md:grid-cols-[repeat(auto-fit,minmax(221px,1fr))]">
            {availableDialects.map((_dialect) => (
              <DialectCard
                isSelected={_dialect.urlValue === selectedDialect}
                dialect={_dialect}
                onClick={() => setSelectedDialect(_dialect.urlValue)}
                key={_dialect._id}
              />
            ))}
          </div>
        </div>

        <button
          className="mx-auto w-[90%] rounded-[25px] border border-white bg-stg-red-1 p-3 font-bold outline-none disabled:cursor-wait disabled:bg-stg-grey-4"
          onClick={dialectChangeHandler}
          disabled={isSwitching}
        >
          {t("dialect_selector.cta")}
        </button>
      </Column>
    </div>
  );
};

const DialectCard = ({
  isSelected,
  dialect,
  onClick,
}: {
  isSelected: boolean;
  dialect: Dialect;
  onClick: () => void;
}) => {
  const { currentLanguage } = useClientTranslations();
  return (
    <div
      className="relative aspect-video w-full cursor-pointer *:flex md:min-w-[221px] "
      onClick={onClick}
    >
      <div className="absolute left-2 top-2 z-20 rounded-full border border-white bg-transparent p-1">
        <div
          className={`h-2 w-2 rounded-full ${isSelected ? "bg-white" : "bg-transparent"}`}
        />
      </div>
      <Img
        src={`${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/dialect/${dialect.imageUrl}`}
        fill={true}
        alt={dialect.dialect}
        className="w-[100%] rounded-lg border"
        objectFit="cover"
        sizes="30rem"
      />
      <div
        className={`absolute z-10 h-[100%] w-[100%] rounded-lg border-2 bg-black/60 pl-2 pt-8 hover:cursor-pointer ${isSelected ? "border-stg-highlight" : "border-transparent"}`}
      >
        <Text size={4} alignment="left" weight="bold">
          {getDialectLabelFromDialectObj({
            dialect: dialect,
            language: currentLanguage as LangEnum,
          })}
        </Text>
      </div>
    </div>
  );
};
